import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { ticketsAPI, referenceAPI, projectsAPI } from '../../api/api';
import { getProjectsForTicketCreate } from '../../api/projectApi';
import { useFormState } from '../../hooks/useApiRequest';
import { useImageHandler } from '../../hooks/useImageHandler';
import { useFileUpload } from '../../hooks/useImageHandler';
import ImageUploader from '../../components/image/ImageUploader';
import ImageGallery from '../../components/image/ImageGallery';
import ImageEditor from '../../components/image/ImageEditor';
import ImagePreview from '../../components/image/ImagePreview';
import useIsMobile from '../../hooks/useIsMobile';

const CreateTicketModal = ({ isOpen, onClose, onCreateTicket }) => {
  const isMobile = useIsMobile();
  const { selectedProject, projects, loadProjectsByDepartment } = useProject();
  const { user } = useAuth();
  const [ticketCreateProjects, setTicketCreateProjects] = useState([]);

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Custom hook'ları kullan
  const { formData, setFormData, updateField, updateMultipleFields, resetForm } = useFormState({
    title: '',
    description: '',
    ticket_type_id: null,
    priority_id: null,
    assigned_to_user_id: null,
    status_id: null,
    topic_id: null,
    project: selectedProject?.id || null,
    department_id: null,
    hotel_id: user?.hotel?.id || null,
    due_date: ''
  });

  const {
    images, setImages, selectedImage, setSelectedImage, showImageEditor, setShowImageEditor,
    showImagePreview, setShowImagePreview, currentImageIndex, setCurrentImageIndex,
    handleImageEdit, handleImageUpdate, handleImageDelete, handleImagePreview, handleImageNavigate,
    addImages, clearImages
  } = useImageHandler();

  const {
    attachments, setAttachments, dragActive, addFiles, removeAttachment, clearAttachments,
    handleDrag, handleDrop
  } = useFileUpload();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceData, setReferenceData] = useState({
    priorities: [],
    ticketTypes: [],
    topics: [],
    users: [],
    statuses: [],
    departments: [],
    hotels: [] // Bu satırı ekleyin
  });
  const [loading, setLoading] = useState(false);
  const [filteredDepartments, setFilteredDepartments] = useState([]); // Otele göre filtrelenmiş departmanlar

  // Reference data'ları yükle
  useEffect(() => {
    if (isOpen) {
      loadReferenceData();
      // Modal açıldığında ticket create projelerini temizle
      setTicketCreateProjects([]);
    }
  }, [isOpen]);

  // Proje değiştiğinde status'ları yeniden yükle
  useEffect(() => {
    if (isOpen && selectedProject?.id && formData.project !== selectedProject.id) {
      setFormData(prev => ({ ...prev, project: selectedProject.id, status_id: null }));
      loadProjectData(selectedProject.id);
    }
  }, [selectedProject?.id]);

  // Projeler değiştiğinde seçili projeyi kontrol et
  useEffect(() => {
    if (formData.project && ticketCreateProjects && Array.isArray(ticketCreateProjects)) {
      // Eğer seçili proje mevcut projeler listesinde yoksa, proje seçimini temizle
      if (!ticketCreateProjects.some(p => p.id === parseInt(formData.project))) {
        setFormData(prev => ({ ...prev, project: null, status_id: null }));
      }
    }
  }, [ticketCreateProjects, formData.project]);

  // Otel değiştiğinde departmanları filtrele
  useEffect(() => {
    if (formData.hotel_id && referenceData.departments && referenceData.departments.length > 0) {
      const hotelDepartments = referenceData.departments.filter(dept => {
        // Hem string hem number karşılaştırması yap
        return dept.hotel == formData.hotel_id || dept.hotel === parseInt(formData.hotel_id);
      });

      setFilteredDepartments(hotelDepartments);

      // Eğer seçili bir departman varsa ve bu otele ait değilse, departman seçimini temizle
      if (formData.department_id && !hotelDepartments.some(d => d.id === parseInt(formData.department_id))) {
        setFormData(prev => ({ ...prev, department_id: null, project: null, status_id: null }));
      }
    } else {
      setFilteredDepartments([]);
    }
  }, [formData.hotel_id, referenceData.departments]);

  const loadReferenceData = async () => {
    try {
      setLoading(true);
      const [hotelsRes, prioritiesRes] = await Promise.all([
        referenceAPI.getHotels(),
        referenceAPI.getPriorities()
      ]);



      if (hotelsRes.success) {
        // 🚨 TEMPORARY FILTER: Şimdilik sadece Antedon otelini göster
        // TODO: Gelecekte tüm otelleri göstermek için bu filtreyi kaldır
        const filteredHotels = hotelsRes.data.filter(hotel => {
          const hotelName = hotel.hotel_name?.toLowerCase();
          return hotelName?.includes('antedon');
        });

        setReferenceData(prev => ({
          ...prev,
          hotels: filteredHotels, // Filtrelenmiş otelleri kullan
          priorities: prioritiesRes.success ? prioritiesRes.data : [],
          departments: [], // Otel seçilince yüklenecek
          statuses: [], // Proje seçilince yüklenecek
          users: [], // Proje seçilince yüklenecek
          ticketTypes: [],
          topics: []
        }));

        // Kullanıcının otelini varsayılan olarak seç ve departmanları yükle
        if (user?.hotel?.id) {
          // Kullanıcının oteli filtrelenmiş listede var mı kontrol et
          const userHotelInFiltered = filteredHotels.find(h => h.id === user.hotel.id);
          if (userHotelInFiltered) {
            setFormData(prev => ({ ...prev, hotel_id: user.hotel.id }));
            await loadDepartmentsByHotel(user.hotel.id);
          } else if (filteredHotels.length > 0) {
            // Kullanıcının oteli filtrede yoksa ilk oteli seç
            const firstHotel = filteredHotels[0];
            setFormData(prev => ({ ...prev, hotel_id: firstHotel.id }));
            await loadDepartmentsByHotel(firstHotel.id);
          }
        } else if (filteredHotels.length > 0) {
          // Kullanıcının oteli yoksa ilk oteli seç
          const firstHotel = filteredHotels[0];
          setFormData(prev => ({ ...prev, hotel_id: firstHotel.id }));
          await loadDepartmentsByHotel(firstHotel.id);
        }
      }
    } catch (error) {
      //console.error('Reference data yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Yeni fonksiyon: Otele göre departmanları yükle (şimdilik sadece IT departmanı)
  const loadDepartmentsByHotel = async (hotelId) => {
    try {
      const departmentsRes = await referenceAPI.getDepartments(hotelId);

      if (departmentsRes.success) {
        // 🚨 TEMPORARY FILTER: Şimdilik sadece Information Technology departmanını göster
        // TODO: Gelecekte tüm departmanları göstermek için bu filtreyi kaldır
        const filteredDepts = departmentsRes.data.filter(dept => {
          const deptName = dept.department_name?.toLowerCase();
          return deptName?.includes('information technology')
        });

        setReferenceData(prev => ({
          ...prev,
          departments: filteredDepts // Filtrelenmiş departmanları kullan
        }));
        setFilteredDepartments(filteredDepts);
      }
    } catch (error) {
      //console.error('Departmanlar yüklenirken hata:', error);
    }
  };

  const loadProjectData = async (projectId) => {
    try {
      setLoading(true);
      //console.log('🔄 Proje verileri yükleniyor:', { projectId });

      // Status'ları yükle
      try {
        const statusRes = await ticketsAPI.getStatusesByProject(projectId);
        //console.log('📨 Status API Response:', statusRes);

        if (statusRes.success) {
          //console.log('✅ Statuslar yüklendi:', statusRes.data);
          setReferenceData(prev => ({
            ...prev,
            statuses: statusRes.data || []
          }));

          // Default status seç (Beklemede varsa)
          const toDoStatus = statusRes.data?.find(s =>
            s.status_name?.toLowerCase().includes('to do') ||
            s.status_name?.toLowerCase().includes('beklemede')
          );
          if (toDoStatus) {
            //console.log('🎯 Default status seçildi:', toDoStatus);
            setFormData(prev => ({ ...prev, status_id: toDoStatus.id }));
          }
        } else {
          //console.error('❌ Statuslar yüklenemedi:', statusRes);
        }
      } catch (statusError) {
        //console.error('❌ Status API hatası:', statusError);
      }

      // Kullanıcıları yükle (hata olsa bile devam et)
      try {
        const usersRes = await referenceAPI.getUsersByProjectDepartment(projectId);
        //console.log('📨 Users API Response:', usersRes);

        if (usersRes.success) {
          //console.log('✅ Kullanıcılar yüklendi:', usersRes.data);
          setReferenceData(prev => ({
            ...prev,
            users: usersRes.data || []
          }));
        } else {
          //console.warn('⚠️ Kullanıcılar yüklenemedi (devam ediliyor):', usersRes);
          setReferenceData(prev => ({
            ...prev,
            users: []
          }));
        }
      } catch (usersError) {
        //console.warn('⚠️ Users API hatası (devam ediliyor):', usersError);
        setReferenceData(prev => ({
          ...prev,
          users: []
        }));
      }

    } catch (error) {
      //console.error('❌ Proje verileri yüklenirken genel hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    updateField(name, value);

    // Otel değiştiğinde departmanları yeniden yükle
    if (name === 'hotel_id' && value) {
      // Otel değiştiğinde departman, proje ve status seçimini sıfırla
      updateMultipleFields({ department_id: null, project: null, status_id: null });
      // Ticket create projelerini de temizle
      setTicketCreateProjects([]);
      // Yeni otelin departmanlarını yükle
      await loadDepartmentsByHotel(value);
    }

    // Departman değiştiğinde projeleri yükle
    if (name === 'department_id' && value) {
      // Departman değiştiğinde proje ve status seçimini sıfırla
      updateMultipleFields({ project: null, status_id: null });
      // Departmana göre projeleri yeni ticketcreate API'den yükle
      setLoading(true);
      try {
        //console.log('🔍 TicketCreate API ile projeler yükleniyor...');
        const ticketCreateResponse = await getProjectsForTicketCreate(formData.hotel_id, value);
        //console.log('🔍 TicketCreate API Response:', ticketCreateResponse);

        if (ticketCreateResponse.success) {
          //console.log('✅ TicketCreate API ile projeler yüklendi:', ticketCreateResponse.data);
          // Yeni API'den gelen projeleri ticketCreateProjects state'ine set et
          setTicketCreateProjects(ticketCreateResponse.data || []);
        } else {
          //console.error('❌ TicketCreate API hatası:', ticketCreateResponse);
          setTicketCreateProjects([]);
        }
      } catch (error) {
        //console.error('❌ TicketCreate API ile projeler yüklenirken hata:', error);
        setTicketCreateProjects([]);
      } finally {
        setLoading(false);
      }
    }

    // Proje değiştiğinde status'ları ve kullanıcıları yeniden yükle
    if (name === 'project' && value) {
      loadProjectData(value);
    }
  };

  // Attachment fonksiyonları - Custom hook'tan geliyor
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Görsel handler'ları - Custom hook'tan geliyor
  const handleImagesChange = (newImages) => {
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // FormData kullanarak attachment'ları da gönder
      const submitData = new FormData();

      // Form verilerini ekle
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('project', formData.project);
      submitData.append('status_id', formData.status_id);
      submitData.append('priority_id', formData.priority_id);
      if (formData.ticket_type_id) submitData.append('ticket_type_id', formData.ticket_type_id);
      if (formData.topic_id) submitData.append('topic_id', formData.topic_id);
      if (formData.assigned_to_user_id) submitData.append('assigned_to_user_id', formData.assigned_to_user_id);
      if (formData.due_date) submitData.append('due_date', formData.due_date);

      // Attachment'ları ekle
      attachments.forEach((attachment, index) => {
        submitData.append('attachments', attachment.file);
      });

      // Görselleri ekle
      images.forEach((image, index) => {
        submitData.append('images', image.file);
      });

      // Debug: FormData içeriğini kontrol et

      // 1. ADIM: Ticket oluştur (FormData ile görseller dahil)
      const response = await ticketsAPI.createTicket(submitData);

      if (!response.success) {
        alert(response.message || 'Ticket oluşturulurken hata oluştu!');
        return;
      }

      const ticketData = response.data || response;

      // 2. ADIM: Eğer attachment varsa, ayrı endpoint'e yükle
      if (attachments.length > 0) {
        try {
          const attachmentFormData = new FormData();
          attachments.forEach((attachment) => {
            attachmentFormData.append('files', attachment.file);
          });

          // Ayrı attachment endpoint'ine gönder
          const attachmentResponse = await fetch(`${window.location.origin}/api/tickets/${ticketData.id}/attachments/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            },
            body: attachmentFormData
          });

          const attachmentResult = await attachmentResponse.json();

          if (attachmentResult.success) {
            // Attachments uploaded successfully
          } else {
            // Silently handle error
            // Attachment hatası ticket'ı iptal etmesin
          }
        } catch (attachmentError) {
          // Silently handle error
          // Attachment hatası ticket'ı iptal etmesin
        }
      }

      // 3. ADIM: Başarı işlemleri
      try {
        onCreateTicket(ticketData);
      } catch (onCreateError) {
        // Silently handle error
      }

      // Ticket oluşturuldu event'ini dispatch et
      window.dispatchEvent(new CustomEvent('ticketCreated', { detail: ticketData }));

      // Reset form
      setFormData({
        title: '',
        description: '',
        ticket_type_id: null,
        priority_id: null,
        assigned_to_user_id: null,
        status_id: null,
        topic_id: null,
        project: selectedProject?.id || null,
        due_date: ''
      });

      // Attachment'ları temizle
      setAttachments([]);

      // Görselleri temizle
      setImages([]);
      setSelectedImage(null);
      setShowImageEditor(false);
      setShowImagePreview(false);
      setCurrentImageIndex(0);
      attachments.forEach(attachment => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
      setAttachments([]);

      onClose();
    } catch (error) {
      //console.error('Error creating ticket:', error);
      alert('Ticket oluşturulurken hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-[9999] outline-none focus:outline-none ${isMobile ? 'flex items-end justify-center p-0' : 'flex items-center justify-center p-4'}`}>
      <div className={`bg-white dark:bg-gray-800 overflow-y-auto transform transition-all animate-in fade-in zoom-in duration-300 ${isMobile ? 'rounded-t-2xl w-full max-h-[92dvh]' : 'rounded-2xl max-w-2xl w-full max-h-[90vh]'}`}>
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-dynamic-title font-semibold text-gray-900 dark:text-white">Create New Ticket</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {loading && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Veriler yükleniyor...</p>
            </div>
          )}

          {/* Konum (Otel) Seçimi - YENİ EKLENEN */}
          <div>
            <label className="block font-dynamic-text font-medium text-gray-700 dark:text-gray-300 mb-2">
              Konum (Otel) *
            </label>
            <select
              name="hotel_id"
              value={formData.hotel_id || ''}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Konum seçin...</option>
              {(referenceData.hotels || []).map((hotel) => (
                <option key={hotel.id} value={hotel.id}>
                  {hotel.hotel_name}
                </option>
              ))}
            </select>
          </div>

          {/* Departman Seçimi - GÜNCELLENDİ */}
          <div>
            <label className="block font-dynamic-text font-medium text-gray-700 dark:text-gray-300 mb-2">
              Departman *
            </label>
            <select
              name="department_id"
              value={formData.department_id || ''}
              onChange={handleInputChange}
              required
              disabled={!formData.hotel_id}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Departman seçin...</option>
              {(filteredDepartments || []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.department_name}
                </option>
              ))}
            </select>
            {!formData.hotel_id && (
              <p className="text-xs text-amber-600 mt-1">Önce konum (otel) seçmelisiniz</p>
            )}
          </div>

          {/* Project Selection - DEĞİŞLDİ */}
          <div>
            <label className="block font-dynamic-text font-medium text-gray-700 dark:text-gray-300 mb-2">
              Proje *
            </label>
            <select
              name="project"
              value={formData.project || ''}
              onChange={handleInputChange}
              required
              disabled={!formData.department_id}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Proje seçin...</option>
              {(ticketCreateProjects || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.project_name} ({project.project_code})
                </option>
              ))}
            </select>
            {!formData.department_id && (
              <p className="text-xs text-amber-600 mt-1">Önce departman seçmelisiniz</p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block font-dynamic-text font-medium text-gray-700 dark:text-gray-300 mb-2">
              Başlık *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              placeholder="Ticket başlığını girin"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Açıklama
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              placeholder="Sorunu veya talebi detaylı olarak açıklayın (Opsiyonel)"
            />
          </div>

          {/* Priority and Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Öncelik *
              </label>
              <select
                name="priority_id"
                value={formData.priority_id || ''}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Öncelik seçin...</option>
                {(referenceData.priorities || []).map((priority) => (
                  <option key={priority.id} value={priority.id}>
                    {priority.priority_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Durum *
              </label>
              <select
                name="status_id"
                value={formData.status_id || ''}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Durum seçin...</option>
                {(referenceData.statuses || []).map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.status_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Atanan Kişi
            </label>
            <select
              name="assigned_to_user_id"
              value={formData.assigned_to_user_id || ''}
              onChange={handleInputChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Atanmamış</option>
              {(referenceData.users || []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Due Date - Şimdilik kullanılmıyor */}
          {/* 
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bitiş Tarihi
            </label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleInputChange}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          */}

          {/* Görsel Yükleme */}
          <div>
            <label className="block font-dynamic-text font-medium text-gray-700 dark:text-gray-300 mb-2">
              Görseller
            </label>
            <ImageUploader
              onImagesChange={handleImagesChange}
              maxImages={10}
              maxFileSize={10 * 1024 * 1024} // 10MB
              acceptedTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
            />

            {/* Görsel Galeri */}
            {images.length > 0 && (
              <div className="mt-4">
                <ImageGallery
                  images={images}
                  onImageUpdate={handleImageEdit}
                  onImageDelete={handleImageDelete}
                  onImagePreview={handleImagePreview}
                  editable={true}
                  gridCols="grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                />
              </div>
            )}
          </div>

          {/* Attachments - Diğer Dosyalar */}
          <div>
            <label className="block font-dynamic-text font-medium text-gray-700 dark:text-gray-300 mb-2">
              Diğer Dosyalar (PDF, DOC, vb.)
            </label>

            {/* File Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                accept="image/*,.pdf,.doc,.docx,.txt"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center">
                  <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <span className="font-medium text-blue-600 dark:text-blue-400">Dosya seçin</span> veya sürükleyip bırakın
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    PNG, JPG, PDF, DOC (Maks. 10MB)
                  </p>
                </div>
              </label>
            </div>

            {/* Uploaded Files List */}
            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Yüklenen Dosyalar ({attachments.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {attachment.preview ? (
                          <img
                            src={attachment.preview}
                            alt={attachment.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {attachment.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(attachment.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(attachment.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Dosyayı kaldır"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick Templates */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Hızlı Şablonlar</h3>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  const highPriority = referenceData.priorities.find(p =>
                    p.priority_name?.toLowerCase().includes('high') ||
                    p.priority_name?.toLowerCase().includes('yüksek')
                  );
                  setFormData(prev => ({
                    ...prev,
                    title: 'Bug: ',
                    priority_id: highPriority?.id || prev.priority_id,
                    description: 'Adımlar:\n1. \n2. \n3. \n\nBeklenen davranış:\n\nGerçek davranış:\n\nOrtam:',
                  }));
                }}
                className="text-sm text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 underline"
              >
                Bug Raporu Şablonu
              </button>
              <br />
              <button
                type="button"
                onClick={() => {
                  const mediumPriority = referenceData.priorities.find(p =>
                    p.priority_name?.toLowerCase().includes('medium') ||
                    p.priority_name?.toLowerCase().includes('orta')
                  );
                  setFormData(prev => ({
                    ...prev,
                    title: 'Özellik: ',
                    priority_id: mediumPriority?.id || prev.priority_id,
                    description: 'Bir [kullanıcı türü] olarak, [fayda] için [işlevsellik] istiyorum.\n\nKabul Kriterleri:\n- [ ] \n- [ ] \n- [ ] ',
                  }));
                }}
                className="text-sm text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 underline"
              >
                Özellik Talebi Şablonu
              </button>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 font-dynamic-text text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={!formData.title.trim() || !formData.project || !formData.priority_id || !formData.status_id || isSubmitting || loading}
              className="px-4 py-2 font-dynamic-text bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Oluşturuluyor...' : 'Ticket Oluştur'}
            </button>
          </div>
        </form>
      </div>

      {/* Görsel Düzenleme Modal'ı */}
      <ImageEditor
        image={selectedImage}
        isOpen={showImageEditor}
        onClose={() => {
          setShowImageEditor(false);
          setSelectedImage(null);
        }}
        onSave={handleImageUpdate}
      />

      {/* Görsel Önizleme Modal'ı */}
      <ImagePreview
        images={images}
        currentIndex={currentImageIndex}
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        onNavigate={handleImageNavigate}
      />
    </div>,
    document.body
  );
};

export default CreateTicketModal;

